import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { apiRequest } from "../../../hooks/apiRequest";
import { request } from "../../../utils/request";
import { formatDateTime, toast } from "../../../utils/helpers";
import Loader from "../../general/Loader";

const estados = {
	pendiente: { label: "En cola", clase: "badge-warning" },
	activo: { label: "Activo", clase: "badge-success" },
	agotado: { label: "Agotado", clase: "badge-secondary" },
};

const formatFecha = (fecha) => {
	if (!fecha) return "—";
	const [y, m, d] = fecha.substring(0, 10).split("-");
	return `${d}/${m}/${y}`;
};

const primerError = (response, fallback) =>
	Object.values(response?.data?.message || {})[0]?.[0] || fallback;

/**
 * Rangos autorizados de una numeración: la única forma de cambiar su número
 * final y su vencimiento. Editar el rango activo cambia la numeración; un rango
 * "En cola" se activa solo cuando se agota el activo.
 *
 * Se renderiza dentro del <form> de Edit.js: por eso los inputs no tienen `name`
 * (no entran en el FormData de la numeración) y los botones son type="button".
 */
const Rangos = ({ numeracionID, onCambio }) => {
	const [rangos, setRangos] = useState(null);
	const [nuevo, setNuevo] = useState({ numero_final: "", fecha_vencimiento: "" });
	const [editando, setEditando] = useState(null); // { id, numero_final, fecha_vencimiento }
	const [errors, setErrors] = useState({});
	const [saving, setSaving] = useState(false);

	const getRangos = (notificar = false) => {
		request(`numeracion-documentos/${numeracionID}/rangos`).then((res) => {
			const lista = res?.data || [];
			setRangos(lista);
			if (notificar) onCambio?.(lista);
		});
	};

	useEffect(() => {
		if (numeracionID) getRangos();
	}, [numeracionID]);

	const sinRangos = rangos?.length === 0;
	const siguienteInicial = rangos?.length
		? Math.max(...rangos.map((r) => Number(r.numero_final))) + 1
		: null;

	const agregar = () => {
		setSaving(true);
		const data = new FormData();
		data.append("numero_final", nuevo.numero_final);
		data.append("fecha_vencimiento", nuevo.fecha_vencimiento);

		apiRequest(data, `numeracion-documentos/${numeracionID}/rangos`, "post").then(
			(response) => {
				setSaving(false);
				if (response?.success) {
					setNuevo({ numero_final: "", fecha_vencimiento: "" });
					setErrors({});
					getRangos(true);
					toast(
						response.data.estado === "activo"
							? "Rango agregado y activado."
							: "Rango agregado. Se aplicará cuando se agote el rango actual."
					);
				} else if (response?.code === 422) {
					setErrors({ ...response.data.message });
				}
			}
		);
	};

	const guardarEdicion = () => {
		setSaving(true);
		const data = new FormData();
		data.append("_method", "put");
		data.append("numero_final", editando.numero_final);
		data.append("fecha_vencimiento", editando.fecha_vencimiento || "");

		apiRequest(data, `numeracion-documentos/${numeracionID}/rangos/${editando.id}`, "post").then(
			(response) => {
				setSaving(false);
				if (response?.success) {
					setEditando(null);
					getRangos(true);
					toast("Rango actualizado.");
				} else if (response?.code === 422) {
					toast(primerError(response, "No se pudo actualizar el rango."), "error");
				}
			}
		);
	};

	const eliminar = (rango) => {
		apiRequest(null, `numeracion-documentos/${numeracionID}/rangos/${rango.id}`, "delete").then(
			(response) => {
				if (response?.success) {
					getRangos(true);
					toast("Rango eliminado.");
				} else if (response?.code === 422) {
					toast(primerError(response, "No se pudo eliminar el rango."), "error");
				}
			}
		);
	};

	const descripcion = (rango) => {
		const cuando = rango.activado_at
			? `Activado ${formatDateTime(rango.activado_at)}`
			: `Agregado ${formatDateTime(rango.created_at)}`;
		const quien = rango.usuario ? ` por ${rango.usuario.nombre} ${rango.usuario.apellido || ""}` : "";
		return (cuando + quien).trim();
	};

	const errorNuevo =
		(errors.numero_final || errors.numero_inicial || errors.fecha_vencimiento)?.[0];

	const accion = "btn btn-link btn-sm p-0 ml-2 text-secondary";

	return (
		<div className="popup__body">
			{rangos === null ? (
				<Loader type="small" />
			) : (
				<table className="table table-sm mb-0" style={{ fontSize: "0.875rem" }}>
					<thead>
						<tr className="text-muted">
							<th className="border-top-0 font-weight-normal">Rango</th>
							<th className="border-top-0 font-weight-normal">Vence</th>
							<th className="border-top-0 font-weight-normal">Estado</th>
							<th className="border-top-0" style={{ width: 70 }}></th>
						</tr>
					</thead>
					<tbody>
						{sinRangos && (
							<tr>
								<td colSpan={4} className="text-muted text-center py-2">
									Sin rangos. El primero que agregue quedará activo.
								</td>
							</tr>
						)}
						{rangos.map((rango) =>
							editando?.id === rango.id ? (
								<tr key={rango.id}>
									<td className="align-middle text-nowrap">
										{rango.numero_inicial} –{" "}
										<input
											type="number"
											min={rango.numero_inicial}
											className="form-control form-control-sm d-inline-block"
											style={{ width: 130 }}
											value={editando.numero_final}
											onChange={(e) =>
												setEditando((r) => ({ ...r, numero_final: e.target.value }))
											}
										/>
									</td>
									<td className="align-middle">
										<input
											type="date"
											className="form-control form-control-sm"
											value={editando.fecha_vencimiento || ""}
											onChange={(e) =>
												setEditando((r) => ({ ...r, fecha_vencimiento: e.target.value }))
											}
										/>
									</td>
									<td className="align-middle">
										<span className={`badge ${estados[rango.estado]?.clase}`}>
											{estados[rango.estado]?.label}
										</span>
									</td>
									<td className="align-middle text-right text-nowrap">
										{saving ? (
											<Loader type="small" />
										) : (
											<>
												<button
													type="button"
													className={accion + " text-success"}
													title="Guardar"
													disabled={!editando.numero_final}
													onClick={guardarEdicion}
												>
													<i className="fas fa-check"></i>
												</button>
												<button
													type="button"
													className={accion}
													title="Cancelar"
													onClick={() => setEditando(null)}
												>
													<i className="fas fa-times"></i>
												</button>
											</>
										)}
									</td>
								</tr>
							) : (
								<tr
									key={rango.id}
									className={rango.estado === "agotado" ? "text-muted" : ""}
								>
									<td className="align-middle text-nowrap">
										{rango.numero_inicial} – {rango.numero_final}
									</td>
									<td className="align-middle">{formatFecha(rango.fecha_vencimiento)}</td>
									<td className="align-middle">
										<span
											className={`badge ${estados[rango.estado]?.clase || "badge-light"}`}
											title={descripcion(rango)}
										>
											{estados[rango.estado]?.label || rango.estado}
										</span>
									</td>
									<td className="align-middle text-right text-nowrap">
										{rango.estado !== "agotado" && (
											<button
												type="button"
												className={accion}
												title="Editar rango"
												onClick={() =>
													setEditando({
														id: rango.id,
														numero_final: rango.numero_final,
														fecha_vencimiento: rango.fecha_vencimiento?.substring(0, 10) || "",
													})
												}
											>
												<i className="fas fa-pen"></i>
											</button>
										)}
										{rango.estado === "pendiente" && (
											<button
												type="button"
												className={accion}
												title="Eliminar rango"
												onClick={() => eliminar(rango)}
											>
												<i className="fas fa-trash-alt"></i>
											</button>
										)}
									</td>
								</tr>
							)
						)}
					</tbody>
					<tfoot>
						<tr>
							<td className="align-middle text-nowrap">
								<span className="text-muted">
									{siguienteInicial ? `${siguienteInicial} –` : "Hasta"}
								</span>{" "}
								<input
									type="number"
									min={siguienteInicial || 0}
									placeholder="Número final"
									className="form-control form-control-sm d-inline-block"
									style={{ width: 130 }}
									value={nuevo.numero_final}
									onChange={(e) => setNuevo((n) => ({ ...n, numero_final: e.target.value }))}
								/>
							</td>
							<td className="align-middle">
								<input
									type="date"
									className="form-control form-control-sm"
									value={nuevo.fecha_vencimiento}
									onChange={(e) => setNuevo((n) => ({ ...n, fecha_vencimiento: e.target.value }))}
								/>
							</td>
							<td colSpan={2} className="align-middle text-right">
								{saving && !editando ? (
									<Loader type="small" />
								) : (
									<button
										type="button"
										className="btn btn-sm btn-secondary"
										disabled={!nuevo.numero_final}
										onClick={agregar}
									>
										<i className="fas fa-plus mr-1"></i>
										{sinRangos ? "Agregar" : "Agregar a la cola"}
									</button>
								)}
							</td>
						</tr>
						{errorNuevo && (
							<tr>
								<td colSpan={4} className="border-top-0 pt-0">
									<small className="help-blockParams form-Text">{errorNuevo}</small>
								</td>
							</tr>
						)}
					</tfoot>
				</table>
			)}

            <small className="d-block text-muted mb-2 mt-3 alert alert-warning">
				Número final y vencimiento salen del rango activo. Los rangos en cola se
				activan solos al agotarse el activo.
			</small>
		</div>
	);
};

Rangos.propTypes = {
	numeracionID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
	// Recibe la lista de rangos tras cada cambio.
	onCambio: PropTypes.func,
};

export default Rangos;
